// Town names below z10 — our own extract (data/towns.geojson: the tiles' z9 places
// with Wikidata populations), because the z6 tiles only carry 18 places and the
// default view is z6.2. At z10 the basemap's own places_locality (towns *and*
// villages) takes over; it is moved to the top of the stack so it beats the pins
// for space there too.
//
// Every town carries a label **tier** (assigned in scripts/build-towns.mjs — cities,
// then county towns by hand, then population bands; commuter-belt suburbs demoted),
// and the tier decides the zoom at which the name appears. Names are drawn the way a
// basemap draws them — plain grey, haloed in the land colour, centred on the town,
// no marker — so they never read as one of our features, which always have a
// coloured icon and a label in the layer's own colour (overlays.ts).
//
//   tier 1  cities                 island view   uppercase, medium     above the headline sites
//   tier 2  principal towns        island view   uppercase, spaced     above the headline sites
//   tier 3  other county towns     z6.8          mixed case            below the headline sites
//   tier 4  ≥ 8k                   z7.5
//   tier 5  ≥ 3.5k                 z8.2
//   tier 6  ≥ 1.5k                 z9
//   tier 7  the rest               z9.5
import type { ExpressionSpecification, GeoJSONSource, Map as MLMap, SymbolLayerSpecification } from "maplibre-gl";
import { DATA_BASE } from "../data/loader";
import { FIRST_RANK3_ID } from "./overlays";

export const TOWNS_SRC = "towns";
const HANDOFF = 10;
const FILE = "towns.geojson";
const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

/** Fetched once and kept, so a theme swap (which rebuilds the style) doesn't refetch. */
let cache: GeoJSON.FeatureCollection | null = null;
let pending: Promise<GeoJSON.FeatureCollection> | null = null;

function load(): Promise<GeoJSON.FeatureCollection> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = fetch(DATA_BASE + FILE, { cache: "default" })
      .then((r) => { if (!r.ok) throw new Error(`${FILE}: HTTP ${r.status}`); return r.json() as Promise<GeoJSON.FeatureCollection>; })
      .then((fc) => { cache = fc; return fc; })
      .catch((err) => { console.warn("towns not available:", err); pending = null; return EMPTY; });
  }
  return pending;
}

interface Tier {
  tier: number;
  minzoom: number;
  /** [size at z5, size at z7.5, size just before the handoff] in px. */
  size: [number, number, number];
  caps?: boolean;
  medium?: boolean;
}

// Type sizes: caps read larger than their px, so tier 2 is set smaller than tier 3.
const TIERS: Tier[] = [
  { tier: 1, minzoom: 0, size: [12.5, 14.5, 16.5], caps: true, medium: true },
  { tier: 2, minzoom: 0, size: [10, 11.5, 13], caps: true },
  { tier: 3, minzoom: 6.8, size: [10.5, 11.5, 13] },
  { tier: 4, minzoom: 7.5, size: [10, 11, 12.5] },
  { tier: 5, minzoom: 8.2, size: [9.5, 10.5, 12] },
  { tier: 6, minzoom: 9, size: [9.5, 10.5, 11.5] },
  { tier: 7, minzoom: 9.5, size: [9, 10, 11] },
];
/** Tiers placed before the headline sites (added above them). */
const ABOVE_SITES = new Set([1, 2]);

const townId = (t: number) => `towns-t${t}`;

function townLayer(t: Tier, theme: "light" | "dark", compact: boolean): SymbolLayerSpecification {
  const d = compact ? -1 : 0;
  const big = t.tier <= 3;
  const size: ExpressionSpecification = ["interpolate", ["linear"], ["zoom"], 5, t.size[0] + d, 7.5, t.size[1] + d, 9.9, t.size[2] + d];
  return {
    id: townId(t.tier),
    type: "symbol",
    source: TOWNS_SRC,
    minzoom: t.minzoom,
    maxzoom: HANDOFF,
    filter: ["==", ["get", "tier"], t.tier],
    layout: {
      "text-field": ["get", "name"],
      "text-font": [t.medium ? "Noto Sans Medium" : "Noto Sans Regular"],
      "text-size": size,
      "text-transform": t.caps ? "uppercase" : "none",
      "text-letter-spacing": t.tier === 1 ? 0.06 : t.caps ? 0.09 : 0,
      // Centred on the town when there is room; otherwise just above or below it,
      // and only then beside it — so a name can still be read as "this town".
      "text-variable-anchor": ["center", "top", "bottom", "right", "left"],
      "text-radial-offset": 0.25,
      "text-justify": "auto",
      "text-max-width": t.tier === 1 ? 14 : 8,
      // Room around a town name; the small tiers pack tighter.
      "text-padding": big ? 3 : 2,
      // Within a tier the bigger town is placed first.
      "symbol-sort-key": ["-", 0, ["get", "pop"]],
    },
    paint: {
      // The basemap's own city_label grey (#5c5c5c) for the towns that matter; the
      // small tiers a shade lighter. Halo in the land colour, not white: these names
      // belong to the map, the features sit on it.
      "text-color": theme === "dark" ? (big ? "#b3bac4" : "#8f97a2") : big ? "#4f545b" : "#6f747b",
      "text-halo-color": theme === "dark" ? "#1f1f1f" : "#e2dfda",
      "text-halo-width": 1.4,
    },
  };
}

/** Add the source + layers (idempotent). Call after ensureOverlays on every style load. */
export function ensureTowns(map: MLMap, theme: "light" | "dark", compact = false): void {
  if (map.getSource(TOWNS_SRC)) return;
  map.addSource(TOWNS_SRC, { type: "geojson", data: cache ?? EMPTY });
  if (!cache) {
    void load().then((fc) => { (map.getSource(TOWNS_SRC) as GeoJSONSource | undefined)?.setData(fc); });
  }
  const below = map.getLayer(FIRST_RANK3_ID) ? FIRST_RANK3_ID : undefined;
  // Smallest tier first, so within each group the bigger towns end up on top (placed first).
  const ordered = [...TIERS].sort((a, b) => b.tier - a.tier);
  for (const t of ordered) if (!ABOVE_SITES.has(t.tier)) map.addLayer(townLayer(t, theme, compact), below);
  for (const t of ordered) if (ABOVE_SITES.has(t.tier)) map.addLayer(townLayer(t, theme, compact));
}

/**
 * Hand the basemap's town/village labels the top of the stack from z10, so they are
 * placed before our pins. Call last, after every overlay has been added.
 */
export function raiseBasemapTowns(map: MLMap): void {
  const id = "places_locality";
  if (!map.getLayer(id)) return;
  map.setLayerZoomRange(id, HANDOFF, 24);
  map.moveLayer(id);
}
